import React from "react";
import { useNavigate } from "react-router-dom";
import { useCustomerAuth } from "../AuthContext";
import { Loader2, LogOut, Mail, Phone, User as UserIcon, ShieldCheck } from "lucide-react";

export default function Profile() {
  const { user, loading, logout } = useCustomerAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!loading && !user) navigate("/sign-in", { state: { from: "/profile" } });
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <div className="py-16 text-center text-gray-400">
        <Loader2 className="animate-spin inline mr-2" size={18} /> Loading...
      </div>
    );
  }

  const initials = (user.name || user.email || "U")
    .trim()
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <p className="dams-brand-sub text-red-500 mb-1">Account</p>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">My profile</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/my-bookings")}
            className="pill-btn outline"
            style={{ padding: "0.55rem 1rem" }}
            data-testid="goto-my-bookings"
          >
            My bookings
          </button>
          <button
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            className="pill-btn ghost inline-flex items-center gap-1"
            style={{ padding: "0.55rem 1rem" }}
            data-testid="logout-btn"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      <div className="card-soft p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-red-500 text-white text-xl font-bold flex items-center justify-center">
            {initials}
          </div>
          <div>
            <p className="text-lg font-bold">{user.name}</p>
            <p className="text-sm text-gray-500">Member since {String(user.created_at || "").slice(0, 10)}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field icon={<Mail size={14} />} label="Email" value={user.email} />
          <Field icon={<Phone size={14} />} label="Phone" value={user.phone || "—"} />
          <Field icon={<UserIcon size={14} />} label="Role" value={user.role || "user"} />
          <Field
            icon={<ShieldCheck size={14} />}
            label="Terms accepted"
            value={user.terms_accepted_at ? "Yes" : "—"}
          />
        </div>
        <p className="text-xs text-gray-500 mt-6">
          To update your details, contact us at{" "}
          <a href="mailto:info@damsrentacar.com" className="text-red-600 hover:text-red-700">
            info@damsrentacar.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}

function Field({ icon, label, value }) {
  return (
    <div>
      <p className="field-label inline-flex items-center gap-1.5">
        {icon} {label}
      </p>
      <p className="font-semibold text-gray-900">{value}</p>
    </div>
  );
}
